import { JsonApiRequestError } from '@farfetched/core';
import { ComboboxItem } from '@mantine/core';
import { combine, sample, Store } from 'effector';
import { debounce } from 'patronum';
import {
	createGetCashdeskAllQuery,
	createGetCashiersAllQuery,
	createGetDeviceAllQuery,
	createGetGamesQuery,
	createGetPlayerAllQuery,
	createPlayerTransactionRegistryQuery,
	PlayerTransaction,
	PlayerTransactionRegistryFilters,
} from 'shared/api';
import {
	createDateFilterModel,
	createRegistryModel,
	DateFilterModel,
	getToday,
	RegistryModel,
	SORT_DIRECTIONS,
} from 'shared/lib';

export type CreatePlayerTransactionRegistryModelParams = {
	$allAccountIds: Store<number[]>;
};

export type PlayerTransactionRegistryModel = {
	registryModel: RegistryModel<
		PlayerTransaction,
		PlayerTransactionRegistryFilters,
		JsonApiRequestError
	>;
	dateFilterModel: DateFilterModel;
	$players: Store<ComboboxItem[]>;
	$devices: Store<ComboboxItem[]>;
	$games: Store<ComboboxItem[]>;
	$cashdesks: Store<ComboboxItem[]>;
	$cashiers: Store<ComboboxItem[]>;
};

export const createPlayerTransactionRegistryModel = ({
	$allAccountIds,
}: CreatePlayerTransactionRegistryModelParams): PlayerTransactionRegistryModel => {
	const query = createPlayerTransactionRegistryQuery();

	const [, currentTimeZone] = getToday();

	const getPlayerAllQuery = createGetPlayerAllQuery();

	const getDeviceAllQuery = createGetDeviceAllQuery();

	const getGamesQuery = createGetGamesQuery();

	const getCashdeskAllQuery = createGetCashdeskAllQuery();

	const getCashiersAllQuery = createGetCashiersAllQuery();

	const registryModel = createRegistryModel<
		PlayerTransaction,
		PlayerTransactionRegistryFilters,
		JsonApiRequestError
	>({
		query,
		filters: {
			playerIds: [],
			deviceIds: [],
			gameIds: [],
			cashdeskIds: [],
			cashierIds: [],
			accountIds: [],
			currencies: [],
			types: [],
			dateFrom: '',
			dateTo: '',
			timeZone: currentTimeZone,
		},
		limit: 10,
		sortField: 'date',
		sortDirection: SORT_DIRECTIONS.DESC,
	});

	const dateFilterModel = createDateFilterModel({
		fromChange: registryModel.filters.dateFrom.setValue,
		toChange: registryModel.filters.dateTo.setValue,
		reset: registryModel.clearFilters,
		optional: true,
	});

	const $filterAccountIds = combine(
		registryModel.filters.accountIds.$value,
		$allAccountIds,
		(selected, all) => (selected.length > 0 ? selected : all),
	);

	const $players = getPlayerAllQuery.$data.map(
		(players) => players?.map(({ id, name }) => ({ value: `${id}`, label: name })) ?? [],
	);

	const $devices = getDeviceAllQuery.$data.map(
		(devices) => devices?.map(({ id, name }) => ({ value: `${id}`, label: name })) ?? [],
	);

	const $games = getGamesQuery.$data.map(
		(games) => games?.map(({ id, title }) => ({ value: `${id}`, label: title })) ?? [],
	);

	const $cashdesks = getCashdeskAllQuery.$data.map(
		(cashdesks) => cashdesks?.map(({ id, name }) => ({ value: `${id}`, label: name })) ?? [],
	);

	const $cashiers = getCashiersAllQuery.$data.map(
		(cashiers) => cashiers?.map(({ id, name }) => ({ value: `${id}`, label: name })) ?? [],
	);

	sample({
		clock: debounce($filterAccountIds, 300),
		source: registryModel.gate.status,
		filter: (isOpen, ids) => isOpen && ids.length > 0,
		fn: (_, ids) => ids,
		target: [
			getPlayerAllQuery.start,
			getDeviceAllQuery.start,
			getCashdeskAllQuery.start,
			getCashiersAllQuery.start,
		],
	});

	sample({
		clock: getPlayerAllQuery.finished.success,
		source: { all: $players, current: registryModel.filters.playerIds.$value },
		fn: ({ all, current }) =>
			all
				.filter((player) => current.includes(Number(player.value)))
				.map((player) => Number(player.value)),
		target: registryModel.filters.playerIds.setValue,
	});

	sample({
		clock: getDeviceAllQuery.finished.success,
		source: { all: $devices, current: registryModel.filters.deviceIds.$value },
		fn: ({ all, current }) =>
			all
				.filter((device) => current.includes(Number(device.value)))
				.map((device) => Number(device.value)),
		target: registryModel.filters.deviceIds.setValue,
	});

	sample({
		clock: getCashdeskAllQuery.finished.success,
		source: { all: $cashdesks, current: registryModel.filters.cashdeskIds.$value },
		fn: ({ all, current }) =>
			all
				.filter((cashdesk) => current.includes(Number(cashdesk.value)))
				.map((cashdesk) => Number(cashdesk.value)),
		target: registryModel.filters.cashdeskIds.setValue,
	});

	sample({
		clock: getCashiersAllQuery.finished.success,
		source: { all: $cashiers, current: registryModel.filters.cashierIds.$value },
		fn: ({ all, current }) =>
			all
				.filter((cashier) => current.includes(Number(cashier.value)))
				.map((cashier) => Number(cashier.value)),
		target: registryModel.filters.cashierIds.setValue,
	});

	sample({
		clock: registryModel.gate.open,
		source: $filterAccountIds,
		filter: (ids) => ids.length > 0,
		target: [
			getPlayerAllQuery.start,
			getDeviceAllQuery.start,
			getCashdeskAllQuery.start,
			getCashiersAllQuery.start,
		],
	});

	sample({
		clock: registryModel.gate.open,
		target: getGamesQuery.start,
	});

	sample({
		clock: registryModel.gate.close,
		target: [
			getPlayerAllQuery.reset,
			getDeviceAllQuery.reset,
			getCashdeskAllQuery.reset,
			getCashiersAllQuery.reset,
			getGamesQuery.reset,
		],
	});

	return {
		registryModel,
		dateFilterModel,
		$players,
		$devices,
		$games,
		$cashdesks,
		$cashiers,
	};
};
